import React, { useRef, useState } from "react";
import {
  Image as ImageIcon,
  Upload,
  Loader2,
  Download,
  Trash2,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MAX_CSV_MB = 5;
const SAMPLE_CSV = `strain_name,image_url,brand
Apple-lato Flower,https://images.dutchie.com/your-photo-1.jpg,Calypso
Blue Dream,https://i.imgur.com/blue-dream.jpg,
Wedding Cake 1g Cart,https://your-cdn.com/wedding-cake-cart.jpg,Backyard`;

/**
 * Admin-only bulk product-photo override tool.
 *
 * Drag-drop a CSV with columns: strain_name, image_url, brand(optional).
 * Every matching product in the DB gets `image_url` overwritten and tagged
 * `image_source = "admin_override"` so future ingests/borrow operations
 * leave it alone.
 *
 * Uses POST /api/admin/product-images/upload-csv on the backend.
 */
export const ProductImagesTab = () => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [clearing, setClearing] = useState(false);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("File must be a .csv");
      return;
    }
    if (f.size > MAX_CSV_MB * 1024 * 1024) {
      toast.error(`CSV exceeds ${MAX_CSV_MB} MB`);
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const onUpload = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/product-images/upload-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(
        `${r.data.updated} products updated · ${r.data.skipped} skipped`,
        { description: r.data.errors?.length ? `${r.data.errors.length} rows had errors` : undefined }
      );
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onClearOverrides = async () => {
    if (!window.confirm(
      "Clear ALL admin photo overrides? Products will fall back to cross-site borrowed photos on next sync."
    )) return;
    setClearing(true);
    try {
      const r = await api.post("/admin/product-images/clear-override");
      toast.success(`Cleared ${r.data.cleared} overrides`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearing(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "budlink-product-images-sample.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <ImageIcon className="w-3.5 h-3.5 text-accent" />
            Product photo overrides
          </div>
          <h3 className="font-serif text-2xl">Upload the right pics</h3>
          <p className="text-sm text-foreground/60 max-w-2xl mt-1">
            Drag-drop a CSV mapping <code className="text-foreground/80">strain_name → image_url</code>.
            Every matching product gets its photo replaced and locked — future
            ingests will <strong>never overwrite</strong> your uploads. Use this for any
            product where the auto-borrowed photo is wrong.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadSample}
            data-testid="product-images-sample-csv"
            className="pill h-9 text-xs"
          >
            <Download className="w-3 h-3" /> Sample CSV
          </button>
          <button
            onClick={onClearOverrides}
            disabled={clearing}
            data-testid="product-images-clear-overrides"
            className="pill h-9 text-xs hover:border-destructive/40 hover:text-destructive"
          >
            {clearing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Trash2 className="w-3 h-3" />
            )}
            Clear overrides
          </button>
        </div>
      </div>

      {/* Drag-drop zone */}
      <div
        data-testid="product-images-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${
          dragOver
            ? "border-primary bg-primary/5"
            : "border-border bg-card hover:border-foreground/30 hover:bg-card/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0])}
          data-testid="product-images-file-input"
        />
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 border border-primary/30 mb-3">
          {file ? (
            <FileSpreadsheet className="w-6 h-6 text-primary" strokeWidth={1.5} />
          ) : (
            <Upload className="w-6 h-6 text-primary" strokeWidth={1.5} />
          )}
        </div>
        {file ? (
          <>
            <div className="font-serif text-xl mb-1" data-testid="product-images-filename">
              {file.name}
            </div>
            <div className="text-xs text-muted-foreground mb-4">
              {(file.size / 1024).toFixed(1)} KB · ready to upload
            </div>
            <div className="flex gap-2 justify-center" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={onUpload}
                disabled={busy}
                data-testid="product-images-upload-btn"
                className="pill pill-primary h-10 text-xs px-5"
              >
                {busy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5" />
                )}
                Upload & override
              </button>
              <button
                onClick={() => {
                  setFile(null);
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className="pill h-10 text-xs"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="font-serif text-xl mb-1">Drop your CSV here</div>
            <div className="text-xs text-muted-foreground">
              or click to browse · max {MAX_CSV_MB} MB
            </div>
          </>
        )}
      </div>

      {/* Result panel */}
      {result && (
        <div
          className="border border-border bg-card rounded-2xl p-5"
          data-testid="product-images-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-300" />
            <h4 className="font-serif text-xl">Override complete</h4>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-secondary/40 rounded-xl px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Updated
              </div>
              <div className="font-serif text-2xl text-emerald-300">{result.updated}</div>
            </div>
            <div className="bg-secondary/40 rounded-xl px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Skipped
              </div>
              <div className="font-serif text-2xl">{result.skipped}</div>
            </div>
            <div className="bg-secondary/40 rounded-xl px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Errors
              </div>
              <div className="font-serif text-2xl text-rose-300">
                {result.errors?.length || 0}
              </div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" /> Row errors
              </div>
              <ul className="text-xs font-mono text-rose-300 space-y-1 max-h-40 overflow-auto">
                {result.errors.map((e, i) => (
                  <li key={i}>· {e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Help / format */}
      <div className="border border-border bg-card/40 rounded-2xl p-5">
        <div className="eyebrow mb-2">CSV format</div>
        <pre className="text-xs font-mono text-foreground/80 bg-secondary/30 rounded-lg p-4 overflow-x-auto">
{`strain_name,image_url,brand
Apple-lato Flower,https://images.dutchie.com/your-photo.jpg,Calypso
Blue Dream,https://i.imgur.com/blue-dream.jpg,
Wedding Cake 1g Cart,https://your-cdn.com/cart.jpg,Backyard`}
        </pre>
        <ul className="text-xs text-muted-foreground mt-3 space-y-1 list-disc pl-5">
          <li><code className="text-foreground/80">strain_name</code> — required. Case-insensitive prefix match (so "Apple-lato" matches "Apple-lato Flower" + "Apple-lato Pre-roll" etc.)</li>
          <li><code className="text-foreground/80">image_url</code> — required. Must start with <code>https://</code>, <code>http://</code>, or <code>/uploads/</code>.</li>
          <li><code className="text-foreground/80">brand</code> — optional. If present, narrows the override to that brand only.</li>
          <li>Trusted CDNs render directly: <code>images.dutchie.com</code>, <code>trulieve.com</code>, <code>sunnyside.shop</code>, <code>imgur.com</code>, <code>cdn.shopify.com</code>, <code>weedmaps.com</code>, <code>leafly.com</code>, <code>linklitepro.com</code>.</li>
        </ul>
      </div>
    </div>
  );
};
